import { useMemo } from 'react';

import type { TaskItemLabel } from '../../../../common/tiptap/extensions/taskList/taskListItemChipTypes';
import type { UpdateTaskItemAttrs } from '../taskItemChipsUtils';
import type { ElementChipCallbackSet } from './useElementChipCallbacks';

/**
 * Label callbacks for task-item chips. Renaming and removal mutate the node's
 * denormalised `labels` cache directly rather than dispatching label redux actions
 * (re-syncing the cache from the canonical label store is a later concern).
 */
export const useLabelTaskItemChipCallbacks = ({
    labels,
    updateAttributes,
    canModifyLabels,
}: {
    labels: TaskItemLabel[];
    updateAttributes: UpdateTaskItemAttrs;
    canModifyLabels: boolean;
}): ElementChipCallbackSet =>
    useMemo(
        () => (chip) => {
            if (chip.type !== 'label') return {};

            const labelId = chip.label._id;
            const removeLabel = () => {
                if (!canModifyLabels) return;

                updateAttributes({ labels: labels.filter((label) => label.labelId !== labelId) });
            };

            return {
                canModify: canModifyLabels,
                onCommitEdit: (name: string) => {
                    if (!canModifyLabels) return;

                    updateAttributes({
                        labels: labels.map((label) => (label.labelId === labelId ? { ...label, name } : label)),
                    });
                },
                onEmptyBackspace: removeLabel,
                onDeleteKeyDown: (event) => {
                    if (!canModifyLabels) return;

                    event.preventDefault();
                    event.stopPropagation();
                    removeLabel();
                },
            };
        },
        [canModifyLabels, labels, updateAttributes],
    );
